import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Stack,
} from '@chakra-ui/react';
import { Formik, FormikErrors } from 'formik';
import PropTypes from 'prop-types';
import { useSnackbar } from 'ui-components';
import Button from '~/components/Button';
import EmailInput from '~/components/EmailInput';
import LinkButton from '~/components/LinkButton';
import Modal from '~/components/Modal/Modal';
import Textarea from '~/components/Textarea';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { captureException } from '~/errorsLogging';
import { isValidEmail } from '~/utils';

const propTypes = {
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export interface FormValues {
  email: string;
  message: string;
}

const validate = ({ email }: { email: string }) => {
  const errors: FormikErrors<FormValues> = {};
  if (!isValidEmail(email)) {
    errors.email = 'Must be a valid email';
  }
  return errors;
};

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (values: { email: string; message: string }) => void;
}

const InviteCreatorModal = ({ isOpen, onCancel, onSubmit }: Props) => {
  const snackbar = useSnackbar();

  const handleFinalSubmit = async ({
    email,
    message,
  }: {
    email: string;
    message: string;
  }) => {
    try {
      await onSubmit({
        email: email.trim(),
        message: message?.trim(),
      });
    } catch (error) {
      captureException(error);
      snackbar.error({
        description: SNACKBAR_ERROR_GENERIC_OPTIONS.description,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <Formik<FormValues>
        initialValues={{ email: '', message: '' }}
        onSubmit={handleFinalSubmit}
        validate={validate}
      >
        {({
          values,
          touched,
          errors,
          isValid: _isValid,
          handleChange,
          handleBlur,
          isSubmitting,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Modal.Header />
            <Modal.CloseButton />
            <Modal.Body>
              <Stack spacing={5}>
                <FormControl isInvalid={!!errors.email && touched.email}>
                  <FormLabel htmlFor="email" paddingBottom={2}>
                    By email:
                  </FormLabel>
                  <EmailInput
                    id="email"
                    name="email"
                    placeholder="email"
                    value={values.email}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.message && touched.message}>
                  <FormLabel
                    htmlFor="message"
                    paddingBottom={2}
                    width="100%"
                    display="flex"
                    alignItems="center"
                  >
                    Add a message to your invitation:
                    <FormHelperText
                      color="primary.300"
                      display="inline-block"
                      fontWeight="normal"
                      margin={0}
                      paddingLeft={1}
                    >
                      (optional)
                    </FormHelperText>
                  </FormLabel>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Extra details, conditions, etc"
                    value={values.message}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <FormErrorMessage>{errors.message}</FormErrorMessage>
                </FormControl>
              </Stack>
            </Modal.Body>
            <Modal.Footer spaceBetween flexDirection="row-reverse">
              <Button
                type="submit"
                colorScheme="primary"
                isLoading={isSubmitting}
                loadingText="Creating"
              >
                Invite
              </Button>
              <LinkButton
                color="primary.500"
                fontWeight="normal"
                onClick={onCancel}
              >
                Cancel
              </LinkButton>
            </Modal.Footer>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

InviteCreatorModal.propTypes = propTypes;

export default InviteCreatorModal;
